import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  viewChild,
} from '@angular/core';

import { ECharts, init } from 'echarts';

import { TranslatePipe } from '@core/i18n-legacy';
import type { JobSkillsSummaryDetailsBasicInfo } from '@core/positions-and-skills-types';
import { Theme } from '@core/theme-util';

import { createChartTooltip } from '../chart-tooltip/create-chart-tooltip';
import { SkillsChartTooltipComponent } from './skills-chart-tooltip/skills-chart-tooltip.component';

@Component({
  selector: 'rt-candidate-skills-chart',
  templateUrl: './candidate-skills-chart.component.html',
  styleUrl: './candidate-skills-chart.component.less',
  imports: [TranslatePipe],
  host: {
    '[style.width.px]': 'size()',
    '[style.height.px]': 'size()',
  },
})
export class CandidateSkillsChartComponent {
  private readonly destroyRef = inject(DestroyRef);

  private readonly chartTooltip = createChartTooltip({
    injector: inject(Injector),
    component: SkillsChartTooltipComponent,
    inputs: () => ({ summary: this.skills(), theme: this.theme() }),
  });

  public skills = input.required<JobSkillsSummaryDetailsBasicInfo>();
  public isHidden = input(false);
  public size = input(150);
  public theme = input.required<Theme>();
  public thickness = input(35);
  public showTooltip = input(false);
  public labelFontSize = input<number | null>(null);

  protected container = viewChild.required<ElementRef<HTMLDivElement>>('chartContainer');

  protected isEmpty = computed(() => {
    const skills = this.skills();

    return (
      !skills.belowRequiredLevelSkills.length &&
      !skills.atRequiredLevelSkills.length &&
      !skills.aboveRequiredLevelSkills.length
    );
  });

  protected chartData = computed(() =>
    this.isEmpty() || this.isHidden()
      ? [
          {
            value: 1,
            itemStyle: {
              color: this.theme().colorBgNeutralStrongDefault,
            },
          },
        ]
      : [
          ...(this.skills().belowRequiredLevelSkills.length
            ? [
                {
                  value: this.skills().belowRequiredLevelSkills.length,
                  itemStyle: {
                    color: this.theme().colorBgCriticalDisabled,
                  },
                },
              ]
            : []),
          ...(this.skills().atRequiredLevelSkills.length
            ? [
                {
                  value: this.skills().atRequiredLevelSkills.length,
                  itemStyle: {
                    color: this.theme().colorBgSuccessDisabled,
                  },
                },
              ]
            : []),
          ...(this.skills().noEvaluatedSkills.length
            ? [
                {
                  value: this.skills().noEvaluatedSkills.length,
                  itemStyle: {
                    color: this.theme().colorBgNeutralStrongDefault,
                  },
                },
              ]
            : []),
          ...(this.skills().aboveRequiredLevelSkills.length
            ? [
                {
                  value: this.skills().aboveRequiredLevelSkills.length,
                  itemStyle: {
                    color: this.theme().colorBgSuccessDefault,
                  },
                },
              ]
            : []),
        ],
  );
  private chartOptions = computed(() => {
    const interactive = this.showTooltip() && !this.isEmpty();

    return {
      tooltip: interactive ? { ...this.chartTooltip, trigger: 'item' } : { show: false },
      legend: { show: false },
      animation: false,
      series: [
        ...(interactive
          ? [
              {
                type: 'pie',
                radius: ['0%', '100%'],
                silent: false,
                emphasis: { disabled: true },
                labelLine: { show: false },
                label: { show: false },
                itemStyle: { color: 'transparent' },
                data: [{ value: 1 }],
              },
            ]
          : []),
        {
          type: 'pie',
          radius: [`${100 - this.thickness()}%`, '100%'],
          avoidLabelOverlap: false,
          ...(this.chartData().length > 1 ? { padAngle: 5 } : {}),
          itemStyle: {
            borderRadius: 4,
          },
          emphasis: {
            disabled: true,
          },
          labelLine: {
            show: false,
          },
          label: { show: false },
          silent: !interactive,
          data: this.chartData(),
        },
      ],
    };
  });

  private chart?: ECharts;
  private _updateChart = effect(() => {
    const chartOptions = this.chartOptions();
    if (chartOptions && this.chart) {
      this.chart.setOption(chartOptions);
    }
  });

  protected ngAfterViewInit() {
    const element = this.container().nativeElement;

    const resizeObserver = new ResizeObserver(() => {
      if (!element.clientWidth || !element.clientHeight) {
        return;
      }

      if (this.chart) {
        this.chart.resize();
      } else {
        this.chart = init(element, undefined, { renderer: 'svg' });
        this.chart.setOption(this.chartOptions());
      }
    });
    resizeObserver.observe(element);

    this.destroyRef.onDestroy(() => {
      resizeObserver.disconnect();
      this.chart?.dispose();
    });
  }
}
